
import type { Task, TaskStatus, TaskTicketRef } from '@/shared/api/sprints';

export function isHandedOff(status: TaskStatus | undefined): boolean {
  return Boolean(status && (status.isDone || status.awaitsConfirmation));
}

export function isOrphaned(task: Pick<Task, 'tickets'>, status: TaskStatus | undefined): boolean {
  const tickets = task.tickets ?? [];
  return tickets.length > 0 && tickets.every((t) => t.finished) && !isHandedOff(status);
}

export function doneStatusFor(statuses: TaskStatus[]): TaskStatus | undefined {
  return [...statuses]
    .filter((s) => s.isDone && !s.isTerminal && !s.archivedAt)
    .sort((a, b) => a.sortOrder - b.sortOrder)[0];
}

export function pendingConfirmation(task: Pick<Task, 'tickets'>): TaskTicketRef[] {
  return (task.tickets ?? []).filter((t) => !t.finished);
}

function ticketName(t: TaskTicketRef): string {
  return t.number != null ? `№${t.number}` : `«${t.subject}»`;
}

export function ticketList(tickets: TaskTicketRef[]): string {
  const names = tickets.map(ticketName);
  if (names.length <= 2) return names.join(' и ');
  return `${names.slice(0, 2).join(', ')} и ещё ${names.length - 2}`;
}

export type EnterCheck = { ok: true } | { ok: false; reason: string };

export function canEnterStatus(task: Pick<Task, 'tickets'>, status: TaskStatus | undefined): EnterCheck {
  if (!status) return { ok: true };
  if (status.isTerminal) {
    return { ok: false, reason: 'Этот статус ставит система, когда все заявки задачи закрыты' };
  }
  if (!status.requiresConfirmation) return { ok: true };
  const pending = pendingConfirmation(task);
  if (!pending.length) return { ok: true };
  return {
    ok: false,
    reason: `«${status.label}» — после подтверждения заявителя: ${ticketList(pending)} ещё не ${
      pending.length === 1 ? 'подтверждена' : 'подтверждены'
    }. Переведите задачу на проверку`,
  };
}

export function isAwaitingConfirmation(task: Pick<Task, 'tickets'>, status: TaskStatus | undefined): boolean {
  return Boolean(status?.awaitsConfirmation) && pendingConfirmation(task).length > 0;
}
